import { WEB_DEPLOYMENT_CONFIG } from "./deployment-config.js";
import type { WebDeploymentConfig } from "./deployment-profile.js";
import { strings } from "./strings.js";
import { SIGNAL_PORT } from "./wire.js";

const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const HOST_ID_LENGTH = 8;

export type DiscoveryKind = "qr" | "code" | "room-list";

export interface HostDiscovery {
  hostId: string;
  kind: DiscoveryKind;
  lanIp?: string;
  signalUrl: string;
  yjsUrl: string;
}

export class DiscoveryError extends Error {}

export function discoveryErrorPresentation(
  error: unknown,
  fallback: string,
): string {
  return error instanceof DiscoveryError ? error.message : fallback;
}

export function normalizeHostId(input: string): string {
  const hostId = input.trim().toUpperCase();
  if (hostId.length !== HOST_ID_LENGTH) {
    throw new DiscoveryError(strings.errors.discovery.hostCodeLength);
  }
  for (const character of hostId) {
    if (!CROCKFORD_ALPHABET.includes(character)) {
      throw new DiscoveryError(
        strings.errors.discovery.invalidHostCodeCharacter,
      );
    }
  }
  return hostId;
}

export function normalizeLanIpv4(input: string): string {
  const parts = input.trim().split(".");
  if (parts.length !== 4)
    throw new DiscoveryError(strings.errors.discovery.invalidLanIp);
  const octets = parts.map((part) => {
    if (!/^\d{1,3}$/u.test(part)) return Number.NaN;
    if (part.length > 1 && part.startsWith("0")) return Number.NaN;
    return Number(part);
  });
  if (
    octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)
  ) {
    throw new DiscoveryError(strings.errors.discovery.invalidLanIp);
  }
  const [first = -1, second = -1] = octets;
  const isPrivate =
    first === 10 ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168);
  if (!isPrivate)
    throw new DiscoveryError(strings.errors.discovery.privateLanIpRequired);
  return octets.join(".");
}

export function buildDiscovery(
  hostId: string,
  kind: DiscoveryKind,
  lanIp?: string,
  deployment: Pick<WebDeploymentConfig, "hostZoneName"> = WEB_DEPLOYMENT_CONFIG,
): HostDiscovery {
  const dnsHost = `c.${hostId.toLowerCase()}.${deployment.hostZoneName}`;
  const endpoint = `wss://${dnsHost}:${SIGNAL_PORT}`;
  return {
    hostId,
    kind,
    ...(lanIp === undefined ? {} : { lanIp }),
    signalUrl: `${endpoint}/signal`,
    yjsUrl: `${endpoint}/yjs`,
  };
}

export function discoveryFromCode(
  input: string,
  deployment: Pick<WebDeploymentConfig, "hostZoneName"> = WEB_DEPLOYMENT_CONFIG,
): HostDiscovery {
  return buildDiscovery(normalizeHostId(input), "code", undefined, deployment);
}

export function discoveryFromRoomCandidate(
  hostIdInput: string,
  deployment: Pick<WebDeploymentConfig, "hostZoneName"> = WEB_DEPLOYMENT_CONFIG,
): HostDiscovery {
  return buildDiscovery(
    normalizeHostId(hostIdInput),
    "room-list",
    undefined,
    deployment,
  );
}

export function discoveryFromDeepLink(
  input: string | URL,
  deployment: Pick<
    WebDeploymentConfig,
    "hostZoneName" | "shareOrigin"
  > = WEB_DEPLOYMENT_CONFIG,
): HostDiscovery | undefined {
  const url =
    input instanceof URL ? input : new URL(input, deployment.shareOrigin);
  const match = /^\/j\/([^/]+)\/?$/u.exec(url.pathname);
  if (match?.[1] === undefined) return undefined;
  const hostId = normalizeHostId(decodeURIComponent(match[1]));
  const ipValue = url.searchParams.get("ip");
  let lanIp: string | undefined;
  if (ipValue !== null) {
    try {
      lanIp = normalizeLanIpv4(ipValue);
    } catch (error) {
      if (!(error instanceof DiscoveryError)) throw error;
    }
  }
  return buildDiscovery(hostId, "qr", lanIp, deployment);
}

export function buildJoinDeepLink(
  hostIdInput: string,
  lanIp?: string,
  deployment: Pick<WebDeploymentConfig, "shareOrigin"> = WEB_DEPLOYMENT_CONFIG,
): string {
  const url = new URL(
    `/j/${encodeURIComponent(normalizeHostId(hostIdInput))}`,
    deployment.shareOrigin,
  );
  if (lanIp !== undefined) url.searchParams.set("ip", normalizeLanIpv4(lanIp));
  return url.href;
}
